"use client";

import { useRouter } from "next/navigation";
import { useEffect, useMemo } from "react";
import { api } from "~/trpc/react";
import MarketingNav from "~/app/components/marketing-nav";
import MarketingFooter from "~/app/components/marketing-footer";
import { HomeCapabilitySections } from "~/app/components/home-capability-sections";
import { SiteGlueHomePage } from "~/components/siteglue/siteglue-home-page";
import { scrollToInboundSection } from "~/lib/inbound-gpt/paths";
import { HomeHeroBuildAgentFlow } from "~/components/marketplace/home-hero-build-agent-flow";
import { getMarketingChrome } from "~/lib/siteglue/marketing-chrome";

export function MoonlightHomePage() {
  const router = useRouter();
  // Prefer host/query; SiteGlue marketing home is server-routed in app/page.tsx so
  // this branch is mainly a safety net (never rely on it for first paint on siteglue.ai).
  const isSiteGlue = getMarketingChrome().brandId === "siteglue";

  const moonlightQuery = api.project.getStaffingAgents.useQuery(
    { listingType: "MOONLIGHT" },
    { enabled: !isSiteGlue },
  );

  const stats = useMemo(
    () => ({
      moonlightAgents: moonlightQuery.data?.agents?.length ?? 0,
      answerRate: 100,
      conversionRate: 42,
      conversations: 12232,
      leads: 7345,
      appointments: 1200,
    }),
    [moonlightQuery.data?.agents?.length],
  );

  useEffect(() => {
    if (isSiteGlue) return;
    const hash = window.location.hash.replace(/^#/, "");
    if (!hash) return;
    const legacyFeatureSections = ["identity", "skills", "integrations", "demo", "agents"];
    if (legacyFeatureSections.includes(hash)) {
      const section = hash === "demo" ? "agents" : hash;
      router.replace(`/features#${section}`);
      return;
    }
    const timer = window.setTimeout(() => scrollToInboundSection(hash), 150);
    return () => window.clearTimeout(timer);
  }, [router, isSiteGlue]);

  if (isSiteGlue) {
    return <SiteGlueHomePage />;
  }

  return (
    <div className="min-h-screen bg-[#faf8f5] dark:bg-slate-950">
      <MarketingNav />

      <section className="px-4 pt-4 md:pt-8">
        <div className="mx-auto flex min-h-[calc(100dvh-4rem)] w-full max-w-5xl flex-col justify-center py-8 md:py-10">
          <HomeHeroBuildAgentFlow variant="standalone" />
        </div>
      </section>

      <section className="border-b border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
        <div className="container mx-auto grid grid-cols-2 gap-4 px-4 py-6 md:grid-cols-3 lg:grid-cols-6 md:gap-6">
          {[
            { label: "Moonlighting Agents", value: stats.moonlightAgents.toLocaleString() },
            { label: "Conversations", value: stats.conversations.toLocaleString() },
            { label: "Leads", value: stats.leads.toLocaleString() },
            { label: "Appointments", value: stats.appointments.toLocaleString() },
            { label: "Avg call answer rate", value: `${stats.answerRate}%` },
            { label: "Conversion Rate", value: `${stats.conversionRate}%` },
          ].map((item) => (
            <div key={item.label} className="text-center md:text-left">
              <p className="text-2xl font-bold text-slate-900 dark:text-white md:text-3xl">
                {item.value}
              </p>
              <p className="text-sm text-slate-500">{item.label}</p>
            </div>
          ))}
        </div>
      </section>

      <HomeCapabilitySections />

      <MarketingFooter isLanding />
    </div>
  );
}
